import { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Hand } from 'lucide-react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface SwipeTutorialOverlayProps {
  isVisible: boolean;
  onDismiss: () => void;
  canSwipeLeft: boolean;
  canSwipeRight: boolean;
}

export const SwipeTutorialOverlay = ({
  isVisible,
  onDismiss,
  canSwipeLeft,
  canSwipeRight,
}: SwipeTutorialOverlayProps) => {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        onDismiss();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onDismiss]);

  if (!isVisible) return null;

  const arrowAnimationLeft = prefersReducedMotion ? "" : "animate-swipe-left";
  const arrowAnimationRight = prefersReducedMotion ? "" : "animate-swipe-right";
  const breathingAnimation = prefersReducedMotion ? "" : "animate-breathing";

  return (
    <div
      role="dialog"
      aria-label="Swipe navigation tutorial"
      aria-describedby="tutorial-description"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      onClick={onDismiss}
    >
      <p id="tutorial-description" className="sr-only">
        Swipe left to go to the next page, swipe right to go to the previous page.
        Tap anywhere to dismiss this tutorial.
      </p>

      <div
        className="relative px-8 py-6 bg-card/90 backdrop-blur-xl rounded-2xl border border-primary/30 shadow-2xl mx-4 max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h3 className="text-2xl font-bold text-center text-foreground mb-2">
          Swipe to Navigate
        </h3>

        {/* Arrow Container */}
        <div className="flex gap-8 items-center justify-center my-8">
          {canSwipeRight && (
            <div className={`flex flex-col items-center gap-2 ${arrowAnimationLeft}`}>
              <ChevronLeft 
                size={48} 
                className="text-primary drop-shadow-[0_0_10px_hsl(var(--primary))]" 
              />
              <span className="text-sm text-muted-foreground font-medium">Previous</span>
            </div>
          )}

          <div className="flex flex-col items-center gap-3 text-center px-4">
            <Hand 
              size={40} 
              className={`text-primary ${breathingAnimation}`} 
            />
            <p className="text-sm text-foreground leading-relaxed">
              Swipe {canSwipeLeft && canSwipeRight ? 'left or right' : canSwipeLeft ? 'left' : 'right'} to move between pages
            </p>
          </div>

          {canSwipeLeft && (
            <div className={`flex flex-col items-center gap-2 ${arrowAnimationRight}`}>
              <ChevronRight 
                size={48} 
                className="text-primary drop-shadow-[0_0_10px_hsl(var(--primary))]" 
              />
              <span className="text-sm text-muted-foreground font-medium">Next</span>
            </div>
          )}
        </div>

        {/* Dismiss hint */}
        <p className="text-sm text-center text-muted-foreground/70 mt-4">
          Tap anywhere to close
        </p>
      </div>
    </div>
  );
};
